// File: src/pages/ProductListCompact.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Button, Input, Spinner } from "reactstrap";
import { useNavigate, Link } from "react-router-dom";
import { FaPlus, FaPencilAlt } from "react-icons/fa";
import { ImDownload } from "react-icons/im";
import { Trash2 } from "react-feather";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

import {
  _Get,
  _Delete,
  _Put,
  _PostSave,
  _GetByUrl,
  AllCategoryList,
  CreateCategory,
  CreateSubCategory,
} from "../import/ApiEndPoint/ApiCalling";

import {
  PurchaseProductList_Product,
  Delete_Product,
  Update_Product,
  saveUnit,
  viewUnits,
} from "../import/ApiEndPoint/Api";

import { CheckPermission } from "../import/CheckPermission";

const injectOnce = () => {
  if (document.getElementById("prod-inline-css")) return;
  const s = document.createElement("style");
  s.id = "prod-inline-css";
  s.textContent = `
    .p-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .p-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .p-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .p-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:1100px; }
    .p-table thead th{ background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em; border-bottom:1px solid #e5e7eb; padding:6px 8px; text-align:center; white-space:nowrap; }
    .p-table tbody td{ padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:13px; text-align:center; }
    .p-table tbody tr:hover{ background:#f9fbff; }
    .actions{ display:flex; justify-content:center; align-items:center; gap:8px; }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; }
    .chip-edit{ background:#d46f10; }
    .chip-del{ background:#ec1809; }
    .chip-link{ color:#0f172a; text-decoration:none; }
    .fix-note{ font-size:12px; color:#475569; font-weight:700; background:#fff7ed; border:1px solid #fed7aa; border-radius:999px; padding:6px 10px; }
    .fix-btn{ background:#0f766e; color:#fff; font-weight:700; height:32px; border:none; }
    .fix-btn:disabled{ opacity:.65; cursor:not-allowed; }
  `;
  document.head.appendChild(s);
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

const cleanText = (v) => toStr(v).trim().replace(/\s+/g, " ");

const normKey = (v) => cleanText(v).toLowerCase();

const hasText = (v) => cleanText(v).length > 0;

const getUser = () => JSON.parse(localStorage.getItem("userData") || "{}");

const trimTrailingSlash = (s) => String(s || "").replace(/\/+$/, "");
const trimLeadingSlash = (s) => String(s || "").replace(/^\/+/, "");

const getObjectId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return (
      value?._id ||
      value?.id ||
      value?.$oid ||
      value?.warehouseId ||
      value?.warehouseId1 ||
      ""
    );
  }
  return String(value);
};

const makeUnitId = (name) => {
  const text = cleanText(name);
  if (!text) return "";

  const only = text.replace(/[^a-zA-Z0-9]/g, "");
  if (only && only.length <= 8) return only;

  const words = text.split(/\s+/).filter(Boolean);
  const short = words.map((w) => w[0]).join("");

  if (short.length >= 2) return short.slice(0, 8);
  if (only) return only.slice(0, 8);

  return text.slice(0, 8);
};

const parseUnitValue = (value) => {
  const text = cleanText(value);
  const m = text.match(/^(.+?)\s*\((.+?)\)\s*$/);

  if (m) {
    return {
      unitName: cleanText(m[1]),
      unitId: cleanText(m[2]),
    };
  }

  return {
    unitName: text,
    unitId: makeUnitId(text),
  };
};

const buildUnitMap = (units = []) => {
  const map = new Map();

  units.forEach((u) => {
    const unitName = cleanText(u?.unitName);
    const unitId = cleanText(u?.unitId);

    const keys = [
      unitName,
      unitId,
      unitName && unitId ? `${unitName}(${unitId})` : "",
    ];

    keys.forEach((k) => {
      const key = normKey(k);
      if (key && !map.has(key)) map.set(key, u);
    });
  });

  return map;
};

const findUnitMatch = (unitMap, rawValue) => {
  if (!hasText(rawValue)) return null;

  const parsed = parseUnitValue(rawValue);
  const keys = [
    rawValue,
    parsed.unitName,
    parsed.unitId,
    parsed.unitName && parsed.unitId
      ? `${parsed.unitName}(${parsed.unitId})`
      : "",
  ];

  for (const k of keys) {
    const found = unitMap.get(normKey(k));
    if (found) return found;
  }

  return null;
};

const buildCategoryMap = (categories = []) => {
  const map = new Map();

  categories.forEach((c) => {
    const key = normKey(c?.name);
    if (key && !map.has(key)) map.set(key, c);
  });

  return map;
};

const buildSubcategoryMap = (category) => {
  const map = new Map();

  const subs = Array.isArray(category?.subcategories)
    ? category.subcategories
    : [];

  subs.forEach((s) => {
    const key = normKey(s?.name);
    if (key && !map.has(key)) map.set(key, s);
  });

  return map;
};

const getProfitColorCode = (p) =>
  p?.profitColorCode || p?.ProfitColorCode || p?.profitColourCode || "";

const createCategoryMaster = async (user, categoryName) => {
  const fd = new FormData();
  fd.append("created_by", user?._id || "");
  fd.append("name", cleanText(categoryName).toUpperCase());
  fd.append("description", "");
  fd.append("status", "Active");

  return CreateCategory(fd);
};

const createSubcategoryMaster = async (user, categoryId, subcategoryName) => {
  const fd = new FormData();
  fd.append("created_by", user?._id || "");
  fd.append("name", cleanText(subcategoryName).toUpperCase());
  fd.append("category", categoryId);
  fd.append("description", "");
  fd.append("status", "Active");

  return CreateSubCategory(fd);
};

export default function ProductListCompact() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [permissions, setPermissions] = useState({});

  const [fixing, setFixing] = useState(false);
  const [fixProgress, setFixProgress] = useState("");

  useEffect(() => injectOnce(), []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const user = getUser();
      setPermissions(CheckPermission("Product Creation") || {});

      const res = await _Get(PurchaseProductList_Product, user?.database);
      const list = Array.isArray(res?.Product)
        ? [...res.Product].reverse()
        : [];

      setRows(list);
    } catch (err) {
      console.error("[ProductList] load error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const loadMasters = async (user) => {
    const [unitRes, catRes] = await Promise.all([
      _GetByUrl(viewUnits, user?.database),
      AllCategoryList(user?._id, user?.database),
    ]);

    return {
      units: Array.isArray(unitRes?.dynamicUnit?.Units)
        ? unitRes.dynamicUnit.Units
        : [],
      categories: Array.isArray(catRes?.Category) ? catRes.Category : [],
    };
  };

  const updateProductMasterFields = async (product, fixedFields, user) => {
    const productId = getObjectId(product?._id || product?.id);

    if (!productId) {
      return {
        status: false,
        message: "Product id missing",
      };
    }

    const fd = new FormData();
    fd.append("database", user?.database || product?.database || "");

    Object.entries(fixedFields).forEach(([key, value]) => {
      fd.append(key, value === undefined || value === null ? "" : value);
    });

    const endpoint = trimTrailingSlash(Update_Product);
    const cleanId = trimLeadingSlash(productId);

    return _Put(endpoint, cleanId, fd);
  };

  const onFixProducts = async () => {
    if (!rows.length) {
      return swal("No products found", "", "warning");
    }

    const confirm = await swal({
      title: "Fix Products?",
      text: "This will create missing units/categories/subcategories and update products to use the exact saved master names.",
      icon: "warning",
      buttons: ["Cancel", "Fix Products"],
    });

    if (!confirm) return;

    const user = getUser();

    if (!user?._id || !user?.database) {
      return swal("Error", "User database details not found", "error");
    }

    setFixing(true);
    setFixProgress("Checking products...");

    const stats = {
      checked: rows.length,
      unitsCreated: 0,
      categoriesCreated: 0,
      subcategoriesCreated: 0,
      productsUpdated: 0,
      alreadyOk: 0,
      skipped: 0,
      failed: 0,
    };

    try {
      let masters = await loadMasters(user);
      let unitMap = buildUnitMap(masters.units);

      const missingUnits = new Map();

      rows.forEach((p) => {
        ["primaryUnit", "secondaryUnit"].forEach((field) => {
          const raw = p?.[field];
          if (!hasText(raw)) return;

          const found = findUnitMatch(unitMap, raw);
          if (found) return;

          const parsed = parseUnitValue(raw);
          const key = normKey(parsed.unitName);

          if (key && !missingUnits.has(key)) {
            missingUnits.set(key, {
              unitName: parsed.unitName,
              unitId: parsed.unitId || makeUnitId(parsed.unitName),
            });
          }
        });
      });

      if (missingUnits.size > 0) {
        setFixProgress(`Creating ${missingUnits.size} missing unit(s)...`);

        const payload = {
          created_by: user?._id,
          database: user?.database,
          Units: Array.from(missingUnits.values()),
        };

        const unitSaveRes = await _PostSave(saveUnit, payload);

        if (unitSaveRes?.status === false) {
          throw new Error(unitSaveRes?.message || "Could not create units");
        }

        stats.unitsCreated = missingUnits.size;

        masters = await loadMasters(user);
        unitMap = buildUnitMap(masters.units);
      }

      let categoryMap = buildCategoryMap(masters.categories);
      const missingCategories = new Map();

      rows.forEach((p) => {
        const rawCat = cleanText(p?.category);
        if (!rawCat) return;

        const found = categoryMap.get(normKey(rawCat));
        if (found) return;

        const key = normKey(rawCat);
        if (key && !missingCategories.has(key)) {
          missingCategories.set(key, rawCat);
        }
      });

      if (missingCategories.size > 0) {
        setFixProgress(
          `Creating ${missingCategories.size} missing category(s)...`,
        );

        for (const categoryName of missingCategories.values()) {
          try {
            await createCategoryMaster(user, categoryName);
            stats.categoriesCreated += 1;
          } catch (err) {
            console.error("[FixProducts] category create failed:", err);
            stats.failed += 1;
          }
        }

        masters = await loadMasters(user);
        categoryMap = buildCategoryMap(masters.categories);
      }

      const missingSubcategories = new Map();

      rows.forEach((p) => {
        const rawCat = cleanText(p?.category);
        const rawSub = cleanText(p?.SubCategory);

        if (!rawSub) return;

        if (!rawCat) {
          stats.skipped += 1;
          return;
        }

        const parentCategory = categoryMap.get(normKey(rawCat));

        if (!parentCategory?._id) {
          stats.skipped += 1;
          return;
        }

        const subMap = buildSubcategoryMap(parentCategory);
        const foundSub = subMap.get(normKey(rawSub));

        if (foundSub) return;

        const key = `${parentCategory._id}|${normKey(rawSub)}`;

        if (!missingSubcategories.has(key)) {
          missingSubcategories.set(key, {
            categoryId: parentCategory._id,
            subcategoryName: rawSub,
          });
        }
      });

      if (missingSubcategories.size > 0) {
        setFixProgress(
          `Creating ${missingSubcategories.size} missing subcategory(s)...`,
        );

        for (const item of missingSubcategories.values()) {
          try {
            await createSubcategoryMaster(
              user,
              item.categoryId,
              item.subcategoryName,
            );
            stats.subcategoriesCreated += 1;
          } catch (err) {
            console.error("[FixProducts] subcategory create failed:", err);
            stats.failed += 1;
          }
        }

        masters = await loadMasters(user);
        categoryMap = buildCategoryMap(masters.categories);
        unitMap = buildUnitMap(masters.units);
      }

      setFixProgress("Updating affected products...");

      for (const p of rows) {
        const fixed = {};
        let changed = false;

        const primaryRaw = cleanText(p?.primaryUnit);
        if (primaryRaw) {
          const match = findUnitMatch(unitMap, primaryRaw);
          const canonical = cleanText(match?.unitName);

          if (canonical && canonical !== primaryRaw) {
            fixed.primaryUnit = canonical;
            changed = true;
          }
        }

        const secondaryRaw = cleanText(p?.secondaryUnit);
        if (secondaryRaw) {
          const match = findUnitMatch(unitMap, secondaryRaw);
          const canonical = cleanText(match?.unitName);

          if (canonical && canonical !== secondaryRaw) {
            fixed.secondaryUnit = canonical;
            changed = true;
          }
        }

        const categoryRaw = cleanText(p?.category);
        let finalCategory = categoryRaw;

        if (categoryRaw) {
          const catMatch = categoryMap.get(normKey(categoryRaw));
          const canonicalCat = cleanText(catMatch?.name);

          if (canonicalCat) {
            finalCategory = canonicalCat;

            if (canonicalCat !== categoryRaw) {
              fixed.category = canonicalCat;
              changed = true;
            }
          }
        }

        const subRaw = cleanText(p?.SubCategory);

        if (subRaw && finalCategory) {
          const catMatch = categoryMap.get(normKey(finalCategory));

          if (catMatch) {
            const subMap = buildSubcategoryMap(catMatch);
            const subMatch = subMap.get(normKey(subRaw));
            const canonicalSub = cleanText(subMatch?.name);

            if (canonicalSub && canonicalSub !== subRaw) {
              fixed.SubCategory = canonicalSub;
              changed = true;
            }
          }
        }

        if (!changed) {
          stats.alreadyOk += 1;
          continue;
        }

        try {
          const res = await updateProductMasterFields(p, fixed, user);

          if (res?.status === false) {
            stats.failed += 1;
          } else {
            stats.productsUpdated += 1;
          }
        } catch (err) {
          console.error("[FixProducts] product update failed:", err);
          stats.failed += 1;
        }
      }

      setFixProgress("");

      await fetchData();

      swal(
        "Fix Completed",
        `Products checked: ${stats.checked}
Products updated: ${stats.productsUpdated}
Units created: ${stats.unitsCreated}
Categories created: ${stats.categoriesCreated}
Subcategories created: ${stats.subcategoriesCreated}
Already OK: ${stats.alreadyOk}
Skipped: ${stats.skipped}
Failed: ${stats.failed}`,
        stats.failed > 0 ? "warning" : "success",
      );
    } catch (err) {
      console.error("[FixProducts] error:", err);
      swal("Error", err?.message || "Could not fix products", "error");
    } finally {
      setFixing(false);
      setFixProgress("");
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return rows;

    const hit = (v) => toStr(v).toLowerCase().includes(term);

    return rows.filter((r) => {
      return (
        hit(r?.Product_Title) ||
        hit(r?.category) ||
        hit(r?.SubCategory) ||
        hit(r?.primaryUnit) ||
        hit(r?.secondaryUnit) ||
        hit(r?.HSN_Code) ||
        hit(r?.GSTRate) ||
        hit(r?.status) ||
        hit(r?.warehouse?.id) ||
        hit(r?.warehouse?._id) ||
        hit(r?.warehouse)
      );
    });
  }, [rows, search]);

  const exportXLSX = () => {
    const data = filtered.map((p, i) => ({
      "S.NO": i + 1,
      "WAREHOUSE ID": toStr(
        p?.warehouse?.id || p?.warehouse?._id || p?.warehouse,
      ),
      "PRODUCT NAME": toStr(p?.Product_Title),
      MRP: p?.Product_MRP !== undefined ? Number(p.Product_MRP).toFixed(2) : "",
      "MIN ALERT": toStr(p?.MIN_stockalert),
      "OPENING STOCK": toStr(p?.Opening_Stock),
      CATEGORY: toStr(p?.category),
      SUBCATEGORY: toStr(p?.SubCategory),
      "PRIMARY UNIT": toStr(p?.primaryUnit),
      "SECONDARY UNIT": toStr(p?.secondaryUnit),
      "HSN CODE": toStr(p?.HSN_Code),
      "GST RATE": toStr(p?.GSTRate),
      STATUS: toStr(p?.status),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.writeFile(wb, "ProductList.xlsx");
  };

  const exportPDF = () => {
    if (filtered.length === 0) {
      return swal("No data to export");
    }

    const doc = new jsPDF("landscape", "mm", "a4", false);

    doc.setTextColor(5, 87, 97);
    doc.setFontSize(12);
    doc.text("Product List", 14, 14);

    const head = [
      [
        "S.NO",
        "WAREHOUSE ID",
        "PRODUCT NAME",
        "MRP",
        "MIN ALERT",
        "OPENING STOCK",
        "CATEGORY",
        "SUBCATEGORY",
        "UNIT",
        "HSN",
        "GST",
        "STATUS",
      ],
    ];

    const body = filtered.map((p, i) => [
      i + 1,
      toStr(p?.warehouse?.id || p?.warehouse?._id || p?.warehouse),
      toStr(p?.Product_Title),
      p?.Product_MRP !== undefined ? Number(p.Product_MRP).toFixed(2) : "",
      toStr(p?.MIN_stockalert),
      toStr(p?.Opening_Stock),
      toStr(p?.category),
      toStr(p?.SubCategory),
      toStr(p?.primaryUnit),
      toStr(p?.HSN_Code),
      toStr(p?.GSTRate),
      toStr(p?.status),
    ]);

    doc.autoTable({
      startY: 20,
      head,
      body,
      styles: { fontSize: 8 },
      headStyles: {
        fillColor: [236, 240, 241],
        textColor: [15, 23, 42],
      },
      margin: { left: 10, right: 10 },
    });

    doc.save("ProductList.pdf");
  };

  const onDelete = (id) => {
    swal("Warning", "Sure You Want to Delete it", {
      buttons: {
        cancel: "Cancel",
        catch: { text: "Delete", value: "delete" },
      },
    }).then(async (value) => {
      if (value === "delete") {
        try {
          await _Delete(Delete_Product, id);
          swal("Deleted", "Product Removed", "success");
          fetchData();
        } catch (err) {
          console.error(err);
          swal("Error", "Could not delete product", "error");
        }
      }
    });
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <Row className="p-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="p-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Product List{" "}
                <span className="count-pill">{filtered.length}</span>
              </div>

              {fixing && (
                <span className="fix-note">
                  {fixProgress || "Fixing products..."}
                </span>
              )}

              <div style={{ flex: "1 1 240px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  className="input-color"
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <Button
                className="fix-btn"
                disabled={fixing || loading}
                onClick={onFixProducts}
                title="Create missing units/categories/subcategories and update products"
              >
                {fixing ? "Fixing..." : "Fix Products"}
              </Button>

              {permissions?.Create && (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                  }}
                  onClick={() => navigate("/app/products/create")}
                >
                  <FaPlus size={12} /> Add Product
                </Button>
              )}

              {permissions?.Download && (
                <>
                  <Button
                    outline
                    color="primary"
                    style={{ height: 32 }}
                    onClick={exportXLSX}
                  >
                    <ImDownload size={14} style={{ marginRight: 6 }} />
                    Export .XLSX
                  </Button>

                  <Button
                    outline
                    color="secondary"
                    style={{ height: 32 }}
                    onClick={exportPDF}
                  >
                    Export .PDF
                  </Button>
                </>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <Card className="p-card" style={{ padding: 8 }}>
            <div className="p-table-wrap">
              <table className="p-table">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>WAREHOUSE ID</th>
                    <th>PRODUCT NAME</th>
                    <th>MRP</th>
                    <th>MIN ALERT</th>
                    <th>OPENING STOCK</th>
                    <th>CATEGORY</th>
                    <th>SUBCATEGORY</th>
                    <th>UNIT</th>
                    <th>HSN CODE</th>
                    <th>GST RATE</th>
                    <th>STATUS</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td
                        colSpan={13}
                        style={{ padding: 18, color: "#6b7280" }}
                      >
                        No products found.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((r, i) => {
                      const color = getProfitColorCode(r) || "#0f172a";

                      return (
                        <tr key={r?._id || i}>
                          <td>{i + 1}</td>
                          <td>
                            {r?.warehouse?.id ||
                              r?.warehouse?._id ||
                              r?.warehouse ||
                              ""}
                          </td>

                          <td style={{ textAlign: "left" }}>
                            <Link
                              className="chip-link"
                              to={`/app/freshlist/house/EditAddProduct/${r?._id}`}
                              style={{
                                color,
                                fontWeight: 700,
                              }}
                            >
                              {r?.Product_Title}
                            </Link>
                          </td>

                          <td>
                            {r?.Product_MRP !== undefined
                              ? Number(r?.Product_MRP).toFixed(2)
                              : ""}
                          </td>

                          <td>{r?.MIN_stockalert}</td>
                          <td>{r?.Opening_Stock}</td>
                          <td>{r?.category}</td>
                          <td>{r?.SubCategory}</td>
                          <td>{r?.primaryUnit}</td>
                          <td>{r?.HSN_Code}</td>
                          <td>{r?.GSTRate}</td>
                          <td>{r?.status}</td>

                          <td>
                            <div className="actions">
                              {permissions?.Edit && (
                                <span
                                  className="chip chip-edit"
                                  title="Edit"
                                  onClick={() =>
                                    navigate(`/app/products/edit/${r?._id}`)
                                  }
                                >
                                  <FaPencilAlt size={14} color="white" />
                                </span>
                              )}

                              {permissions?.Delete && (
                                <span
                                  className="chip chip-del"
                                  title="Delete"
                                  onClick={() => onDelete(r?._id)}
                                >
                                  <Trash2 size={14} color="white" />
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
}
